import { PureComponent } from 'react';

import { Tooltip } from '@ipfabric/ui-components/Tooltip/Tooltip';
import classNames from 'classnames';
import { Button } from 'components/Button/Button';
import { ConfirmDialog } from 'containers/ConfirmDialog/ConfirmDialog';
import get from 'lodash/get';
import PropTypes from 'prop-types';
import { DropTarget } from 'react-dnd';
import handleEvent from 'utils/handleEvent';
import AddRow from './AddRow';
import Column from './Column';
import { WIDGET } from './ItemTypes';
import Widgets from './Widgets';

const rowTarget = {
  drop(props, monitor) {
    const { rowIndex, columnIndex, onMoveColumn } = props;
    const item = monitor.getItem();
    if (item.type === 'column' && item.rowIndex !== rowIndex) {
      onMoveColumn({
        initialLocation: {
          rowIndex: item.rowIndex,
          columnIndex: item.columnIndex,
        },
        destination: {
          rowIndex,
          columnIndex,
        },
        widgetName: item.widgetName,
      });
    }
  },
  canDrop(props, monitor) {
    const item = monitor.getItem();
    const { rowIndex } = props;
    if (item.rowIndex !== rowIndex && item.type === 'column') {
      return props.columnsLength < 3;
    }
    return true;
  },
};

@DropTarget(WIDGET, rowTarget, (connect, monitor) => ({
  connectDropTarget: connect.dropTarget(),
  isOver: monitor.isOver(),
  canDrop: monitor.canDrop(),
}))
export default class Row extends PureComponent {
  static propTypes = {
    addWidgetComponent: PropTypes.func,
    canDrop: PropTypes.bool.isRequired,
    columns: PropTypes.array,
    columnsLength: PropTypes.number.isRequired,
    connectDropTarget: PropTypes.func,
    droppableColumnClass: PropTypes.string,
    editable: PropTypes.bool,
    editableColumnClass: PropTypes.string,
    frameComponent: PropTypes.func,
    isOver: PropTypes.bool.isRequired,
    msg: PropTypes.object.isRequired,
    onAdd: PropTypes.func,
    onAddRow: PropTypes.func.isRequired,
    onEdit: PropTypes.func.isRequired,
    onMove: PropTypes.func,
    onMoveWidget: PropTypes.func,
    onRemove: PropTypes.func,
    onRemoveRow: PropTypes.func.isRequired,
    onSortColumns: PropTypes.func.isRequired,
    onSortWidgets: PropTypes.func.isRequired,
    onToggleConfirmDialog: PropTypes.func.isRequired,
    onUpdateColumnProperty: PropTypes.func.isRequired,
    rowClass: PropTypes.string,
    rowIndex: PropTypes.number,
    titlePlaceholder: PropTypes.string.isRequired,
    widgets: PropTypes.object,
  };

  static defaultProps = {
    rowClass: 'row',
  };

  handleRemoveRowConfirm = handleEvent(() => {
    const { onRemoveRow, rowIndex } = this.props;
    onRemoveRow(rowIndex);
  });

  handleRemoveRow = handleEvent(() => {
    this.props.onToggleConfirmDialog(`removeRow${this.props.rowIndex}`);
  });

  renderRemoveRow = () => {
    const { msg } = this.props;
    return (
      <div className='col-xs-12'>
        <Tooltip content={get(msg, ['dazzle', 'removeRow'])}>
          <Button
            accent='negative'
            className='float-right'
            icon='fa-xmark'
            onClick={this.handleRemoveRow}
            size='xxs'
            variant='tertiary'
            data-testid='dazzle-remove-row-button'
          />
        </Tooltip>
      </div>
    );
  };

  render() {
    const {
      addWidgetComponent,
      canDrop,
      columns,
      columnsLength,
      connectDropTarget,
      droppableColumnClass,
      editable,
      editableColumnClass,
      frameComponent,
      isOver,
      msg,
      onAdd,
      onAddRow,
      onEdit,
      onMove,
      onMoveWidget,
      onRemove,
      onSortColumns,
      onSortWidgets,
      onUpdateColumnProperty,
      rowClass,
      rowIndex,
      titlePlaceholder,
      widgets,
    } = this.props;

    const items = columns.map((column, index) => {
      return (
        <Column
          addWidgetComponent={addWidgetComponent}
          className={column.className}
          columnsLength={columnsLength}
          columnIndex={index}
          columnOrientation={column.columnOrientation}
          droppableColumnClass={droppableColumnClass}
          editable={editable}
          editableColumnClass={editableColumnClass}
          // biome-ignore lint/suspicious/noArrayIndexKey: legacy code
          key={index}
          onAdd={onAdd}
          onMove={onMove}
          onMoveWidget={onMoveWidget}
          onSortColumns={onSortColumns}
          onUpdateColumnProperty={onUpdateColumnProperty}
          rowIndex={rowIndex}
          title={column.title}
          titlePlaceholder={titlePlaceholder}
          msg={msg}
        >
          <Widgets
            columnIndex={index}
            columnsLength={columnsLength}
            columnOrientation={column.columnOrientation}
            editable={editable}
            frameComponent={frameComponent}
            // biome-ignore lint/suspicious/noArrayIndexKey: legacy code
            key={index}
            msg={msg}
            onEdit={onEdit}
            onMove={onMove}
            onRemove={onRemove}
            onSortWidgets={onSortWidgets}
            rowIndex={rowIndex}
            widgets={column.widgets}
            widgetTypes={widgets}
          />
        </Column>
      );
    });

    return connectDropTarget(
      <div>
        {editable && (
          <ConfirmDialog
            id={`removeRow${rowIndex}`}
            msg={get(msg, ['dazzle', 'confirmDeleteRow'])}
            onClickConfirm={this.handleRemoveRowConfirm}
            btnCancelAccent='positive'
            btnCancelVariant='secondary'
            btnConfirmAccent='negative'
            btnConfirmVariant='primary'
          />
        )}
        <div
          className={classNames(
            'bg-none',
            isOver && !canDrop && 'dazzle__row-row-wrapper--over',
            editable && 'dazzle__row-row-wrapper--outline',
            rowClass,
          )}
        >
          {editable && this.renderRemoveRow()}
          <div className='col-xs-12'>
            <div
              data-testid='dazzle-row'
              className={classNames(
                'row',
                'dazzle__row-content-wrapper',
                editable && 'dazzle__row-content-wrapper--editable',
              )}
            >
              {items}
            </div>
          </div>
        </div>
        {editable && (
          <AddRow rowIndex={rowIndex + 1} onAddRow={onAddRow} msg={msg} />
        )}
      </div>,
    );
  }
}
